import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    addVirtualTenantAlarmPaths,
    createVirtualTenant,
    deleteVirtualTenantAlarmPath,
    getUniqueAlarmPaths,
    getVirtualTenantAlarmPaths,
    getVirtualTenantsBySite,
    updateVirtualTenant,
    type UniqueAlarmPathItem,
} from '../../services/siteManagement/siteManagement.service';
import { getProfile } from '../../services/userManagement/userManagement.service';
import { StatusMessage } from '../ui';
import VirtualTenantsList from '../tables/VirtualTenantsList';
import { useTreeRefresh } from '../../contexts/TreeRefreshContext';
import type { VirtualTenantDetail } from '../../services/siteManagement/siteManagement.types';

const SUCCESS_MESSAGE_TIMEOUT_MS = 5000;
const CREATED_TREE_REFRESH_DELAY_MS = 1000;
const UPDATED_TREE_REFRESH_DELAY_MS = 400;
const DELETE_SETTLE_DELAY_MS = 100;
const FOCUS_DELAY_MS = 0;
const MAX_VISIBLE_PATHS = 200;

interface EditableVirtualTenant extends VirtualTenantDetail {
    alarmPathIds?: number[];
    inheritRules?: boolean;
}

interface VirtualTenantsFormProps {
    siteId: string | number;
    parentSiteId?: number;
    initialItems?: VirtualTenantDetail[];
    onCreated?: () => void;
    onDeleted?: () => void;
    onUpdated?: () => void;
    isModal?: boolean;
    isEdit?: boolean;
    editingItem?: EditableVirtualTenant;
}

interface VirtualTenantPayload {
    name: string;
    inheritRules: boolean;
    alarmPathIds: number[];
    parentSiteId?: number;
}

function getInitialName(editingItem?: EditableVirtualTenant): string {
    if (!editingItem) {
        return '';
    }
    return editingItem.name ?? '';
}

function getEditingPathIds(editingItem?: EditableVirtualTenant): number[] {
    if (!editingItem) {
        return [];
    }
    return editingItem.alarmPathIds ?? editingItem.associatedAlarmPaths ?? [];
}

function getEditingInheritance(editingItem: EditableVirtualTenant | undefined, pathIds: number[]): boolean {
    if (!editingItem) {
        return false;
    }
    return editingItem.inheritRules ?? (pathIds.length === 0);
}

function buildVirtualTenantPayload(
    tenantName: string,
    inheritRules: boolean,
    pathIds: number[],
    parentSiteId?: number,
): VirtualTenantPayload {
    const payload: VirtualTenantPayload = {
        name: tenantName.trim(),
        alarmPathIds: [],
        inheritRules,
    };

    if (!inheritRules) {
        payload.alarmPathIds = pathIds;
    }

    if (parentSiteId !== undefined) {
        payload.parentSiteId = parentSiteId;
    }

    return payload;
}

function getCreatedLabel(item: VirtualTenantDetail): string {
    return item.name ?? String(item.id);
}

function getAlarmPathDisplayLabel(path: UniqueAlarmPathItem | undefined, pathId: number): string {
    if (path?.pathToAlarm) {
        return path.pathToAlarm;
    }
    return String(pathId);
}

function getAlarmPathPlaceholder(inheritRules: boolean, selectedPathIds: number[]): string {
    if (inheritRules) {
        return 'Disabled (inheriting from parent site)';
    }
    if (selectedPathIds.length === 0) {
        return 'Select alarm paths…';
    }
    return 'Click to add more…';
}

function getPathButtonStyle(inheritRules: boolean): React.CSSProperties {
    if (inheritRules) {
        return { opacity: 0.6, cursor: 'not-allowed' };
    }
    return { opacity: 1, cursor: 'pointer' };
}

function getAlarmPathContainerClassName(inheritRules: boolean): string {
    if (inheritRules) {
        return 'select2-like select2-like--disabled';
    }
    return 'select2-like';
}

function getFormDataAttributes(isEdit: boolean): Record<string, string> {
    if (isEdit) {
        return { 'data-vt-edit-form': '' };
    }
    return { 'data-vt-form': '' };
}

function getSubmitLabel(submitting: boolean): string {
    if (submitting) {
        return 'Creating…';
    }
    return 'Create Virtual Tenant';
}

function getVisiblePaths(paths: UniqueAlarmPathItem[], pathSearch: string): UniqueAlarmPathItem[] {
    const normalizedSearch = pathSearch.toLowerCase();

    return paths
        .filter(path => (path.pathToAlarm ?? '').toLowerCase().includes(normalizedSearch))
        .slice(0, MAX_VISIBLE_PATHS);
}

async function fetchVirtualTenants(siteId: number): Promise<VirtualTenantDetail[]> {
    const response = await getVirtualTenantsBySite(siteId);
    if (!response?.success) {
        return [];
    }
    return response.data ?? [];
}

async function fetchAlarmPaths(siteId: number): Promise<UniqueAlarmPathItem[]> {
    const response = await getUniqueAlarmPaths(siteId);
    if (!response?.success || !response.data) {
        return [];
    }
    return response.data;
}

function extractNumericPathId(item: unknown): number | undefined {
    if (typeof item === 'number') {
        return item;
    }
    if (typeof item === 'object' && item !== null) {
        const obj = item as Record<string, unknown>;
        const val = obj.alarmPathId ?? obj.id;
        if (typeof val === 'number') {
            return val;
        }
    }
    return undefined;
}

async function replaceAlarmPaths(vtId: number | string, newPathIds: number[]): Promise<void> {
    const existingPathResponse = await getVirtualTenantAlarmPaths(vtId);
    let existingPathIds: number[] = [];

    if (existingPathResponse?.success && existingPathResponse.data) {
        existingPathIds = (existingPathResponse.data as unknown as Array<unknown>)
            .map(extractNumericPathId)
            .filter((pathId): pathId is number => typeof pathId === 'number');
    }

    if (existingPathIds.length > 0) {
        await Promise.allSettled(existingPathIds.map(pathId => deleteVirtualTenantAlarmPath(vtId, pathId)));
        await new Promise<void>(resolve => {
            setTimeout(resolve, DELETE_SETTLE_DELAY_MS);
        });
    }

    if (newPathIds.length > 0) {
        await addVirtualTenantAlarmPaths(vtId, { alarmPathIds: newPathIds });
    }
}

async function refreshTreeWithDelay(refreshTree: () => Promise<void>, delayMs: number): Promise<void> {
    await new Promise<void>(resolve => {
        setTimeout(() => {
            void refreshTree().finally(resolve);
        }, delayMs);
    });
}


interface VirtualTenantsFormFieldErrors {
    name?: string;
    alarmPaths?: string;
}

const VirtualTenantsForm: React.FC<VirtualTenantsFormProps> = ({
    siteId,
    parentSiteId,
    initialItems = [],
    onCreated,
    onDeleted,
    onUpdated,
    isModal = false,
    isEdit = false,
    editingItem,
}) => {
    const { refreshTree } = useTreeRefresh();
    const [items, setItems] = useState<VirtualTenantDetail[]>(initialItems);
    const [name, setName] = useState<string>('');
    const [inheritRules, setInheritRules] = useState<boolean>(false);
    const [paths, setPaths] = useState<UniqueAlarmPathItem[]>([]);
    const [pathSearch, setPathSearch] = useState<string>('');
    const [openPaths, setOpenPaths] = useState<boolean>(false);
    const [selectedPathIds, setSelectedPathIds] = useState<number[]>([]);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errors, setErrors] = useState<VirtualTenantsFormFieldErrors>({});
    const pathWrapperRef = useRef<HTMLDivElement | null>(null);

    const numericSiteId = Number(siteId);
    const effectiveParentSiteId = parentSiteId ?? numericSiteId;
    const visiblePaths = getVisiblePaths(paths, pathSearch);

    useEffect(() => {
        const pathIds = getEditingPathIds(editingItem);
        setName(getInitialName(editingItem));
        setSelectedPathIds(pathIds);
        setInheritRules(getEditingInheritance(editingItem, pathIds));
    }, [editingItem]);

    useEffect(() => {
        let mounted = true;

        if ((initialItems?.length ?? 0) > 0) {
            setItems(initialItems);
            return () => {
                mounted = false;
            };
        }

        void fetchVirtualTenants(numericSiteId)
            .then(nextItems => {
                if (mounted) {
                    setItems(nextItems);
                }
            })
            .catch(() => {
                if (mounted) {
                    setItems([]);
                }
            });

        return () => {
            mounted = false;
        };
    }, [initialItems, numericSiteId]);

    useEffect(() => {
        if (!numericSiteId || Number.isNaN(numericSiteId)) {
            return undefined;
        }

        let mounted = true;

        void fetchAlarmPaths(numericSiteId)
            .then(nextPaths => {
                if (mounted) {
                    setPaths(nextPaths);
                }
            })
            .catch(() => {
                if (mounted) {
                    setPaths([]);
                }
            });

        return () => {
            mounted = false;
        };
    }, [numericSiteId]);

    useEffect(() => {
        const onDocumentClick = (e: MouseEvent) => {
            const targetNode = e.target as Node;
            if (openPaths && pathWrapperRef.current && !pathWrapperRef.current.contains(targetNode)) {
                setOpenPaths(false);
                setPathSearch('');
            }
        };

        document.addEventListener('mousedown', onDocumentClick);

        return () => {
            document.removeEventListener('mousedown', onDocumentClick);
        };
    }, [openPaths]);

    // Validation helper
    const validateFields = useCallback((nextName: string, nextInheritRules: boolean, nextPathIds: number[]): VirtualTenantsFormFieldErrors => {
        const newErrors: VirtualTenantsFormFieldErrors = {};
        if (!nextName?.trim()) {
            newErrors.name = 'Name is required';
        }
        if (!nextInheritRules && nextPathIds.length === 0) {
            newErrors.alarmPaths = 'Please either select at least one alarm path or enable "Inherit rules from parent site"';
        }
        return newErrors;
    }, []);

    const removePathId = useCallback((pathId: number) => {
        setSelectedPathIds(prev => {
            const nextPathIds = prev.filter(selectedId => selectedId !== pathId);
            const newErrors = validateFields(name, inheritRules, nextPathIds);
            setErrors(newErrors);
            setError(Object.values(newErrors)[0] || null);
            return nextPathIds;
        });
    }, [name, inheritRules, validateFields]);

    const addPathId = useCallback((pathId: number) => {
        setSelectedPathIds(prev => {
            const nextPathIds = [...prev, pathId];
            const newErrors = validateFields(name, inheritRules, nextPathIds);
            setErrors(newErrors);
            setError(Object.values(newErrors)[0] || null);
            return nextPathIds;
        });
    }, [name, inheritRules, validateFields]);

    const handleDismissError = useCallback(() => {
        setError(null);
    }, []);

    const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const nextName = e.target.value;
        setName(nextName);
        const newErrors = validateFields(nextName, inheritRules, selectedPathIds);
        setErrors(newErrors);
        setError(Object.values(newErrors)[0] || null);
    }, [inheritRules, selectedPathIds, validateFields]);

    const handleInheritRulesChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;
        setInheritRules(checked);
        let nextPathIds = selectedPathIds;
        if (checked) {
            nextPathIds = [];
            setSelectedPathIds([]);
            setOpenPaths(false);
        }
        const newErrors = validateFields(name, checked, nextPathIds);
        setErrors(newErrors);
        setError(Object.values(newErrors)[0] || null);
    }, [name, selectedPathIds, validateFields]);

    const handleAlarmPathsButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setOpenPaths(prev => !prev);

        if (!openPaths) {
            setTimeout(() => {
                const searchElement = pathWrapperRef.current?.querySelector('.select2-dropdown__search') as HTMLInputElement | null;
                searchElement?.focus();
            }, FOCUS_DELAY_MS);
        }
    }, [openPaths]);

    const handlePathSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setPathSearch(e.target.value);
    }, []);

    // Accept both mouse and keyboard events for chip removal
    const handleRemovePathChipClick = useCallback((
        e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
    ) => {
        e.stopPropagation();
        const rawPathId = (e.currentTarget as HTMLElement).dataset.pathId;
        if (rawPathId) {
            removePathId(Number(rawPathId));
        }
        // Prevent default for keyboard
        if ('key' in e) {
            e.preventDefault();
        }
    }, [removePathId]);

    const handlePathOptionClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const rawPathId = e.currentTarget.dataset.pathId;

        if (!rawPathId) {
            return;
        }

        const pathId = Number(rawPathId);
        if (selectedPathIds.includes(pathId)) {
            removePathId(pathId);
            return;
        }

        addPathId(pathId);
    }, [addPathId, removePathId, selectedPathIds]);

    const handleCreate = useCallback(async (parsedPathIds: number[]) => {
        const profileResponse = await getProfile();
        if (!profileResponse?.success || !profileResponse.data?.id) {
            setError('Unable to retrieve user profile');
            return;
        }

        const payload = buildVirtualTenantPayload(name, inheritRules, parsedPathIds, effectiveParentSiteId);
        const response = await createVirtualTenant(payload);
        const createdItem = response?.data;

        if (response?.success && createdItem) {
            setItems(prev => [createdItem, ...prev]);
            setName('');
            setSelectedPathIds([]);
            setInheritRules(false);
            setError(null);
            setSuccessMessage(`Virtual tenant "${getCreatedLabel(createdItem)}" created`);
            setTimeout(() => setSuccessMessage(null), SUCCESS_MESSAGE_TIMEOUT_MS);
            onCreated?.();
            await refreshTreeWithDelay(refreshTree, CREATED_TREE_REFRESH_DELAY_MS);
            return;
        }

        setError(response?.error?.message ?? 'Failed to create virtual tenant');
    }, [effectiveParentSiteId, inheritRules, name, onCreated, refreshTree]);

    const handleUpdate = useCallback(async (parsedPathIds: number[]) => {
        if (!editingItem) {
            return;
        }

        const payload = buildVirtualTenantPayload(name, inheritRules, parsedPathIds);

        try {
            await replaceAlarmPaths(editingItem.id, payload.alarmPathIds);
        } catch (pathError) {
            console.info('[VirtualTenantsForm] Alarm path update encountered an error', pathError);
        }

        const response = await updateVirtualTenant(editingItem.id, payload);
        if (response?.success && response.data) {
            setSuccessMessage(`Virtual tenant "${name}" updated`);
            setTimeout(() => setSuccessMessage(null), SUCCESS_MESSAGE_TIMEOUT_MS);
            onUpdated?.();
            await refreshTreeWithDelay(refreshTree, UPDATED_TREE_REFRESH_DELAY_MS);
            return;
        }

        setError(response?.error?.message ?? 'Failed to update virtual tenant');
    }, [editingItem, inheritRules, name, onUpdated, refreshTree]);

    const handleSubmit = useCallback(async (e: React.SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        const newErrors: VirtualTenantsFormFieldErrors = {};

        if (!name?.trim()) {
            newErrors.name = 'Name is required';
        }

        const parsedPathIds = selectedPathIds
            .map(Number)
            .filter(pathId => !Number.isNaN(pathId));

        if (!inheritRules && parsedPathIds.length === 0) {
            newErrors.alarmPaths = 'Please either select at least one alarm path or enable "Inherit rules from parent site"';
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            setError(Object.values(newErrors)[0]);
            return;
        }

        setSubmitting(true);

        try {
            if (isEdit && editingItem) {
                await handleUpdate(parsedPathIds);
            } else {
                await handleCreate(parsedPathIds);
            }
        } catch (submitError: unknown) {
            if (submitError instanceof Error) {
                setError(submitError.message);
            } else {
                setError(String(submitError));
            }
        } finally {
            setSubmitting(false);
        }
    }, [editingItem, handleCreate, handleUpdate, inheritRules, isEdit, name, selectedPathIds]);

    return (
        <div>
            {!isModal && (
                <div className="mb-2">
                    <VirtualTenantsList
                        items={items}
                        onItemsChanged={setItems}
                        onError={setError}
                        onDeleted={onDeleted}
                        createdMessage={successMessage}
                        onClearCreatedMessage={() => setSuccessMessage(null)}
                    />
                </div>
            )}

            {!isModal && <h4>Add Virtual Tennant</h4>}

            <form className="form" onSubmit={handleSubmit} {...getFormDataAttributes(isEdit)}>
                {error && (
                    <StatusMessage kind="error" onClose={handleDismissError} className="mt-2">
                        {error}
                    </StatusMessage>
                )}

                <div className="form__group">
                    <label htmlFor="vtName" className="form__label">Name</label>
                    <input
                        id="vtName"
                        className={`form__control ${errors?.name ? "form__control--error" : ""}`}
                        value={name}
                        onChange={handleNameChange}
                        disabled={submitting}
                        autoComplete='off'
                    />
                </div>

                <div className="form__group">
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={inheritRules}
                            onChange={handleInheritRulesChange}
                            disabled={submitting}
                        />
                        <span>Inherit rules from parent site</span>
                    </label>
                    <div className="form__help-text" style={{ marginTop: '4px', fontSize: '0.875rem', color: '#666' }}>
                        When checked, this virtual tenant will inherit all rules from the parent site. Alarm paths cannot be selected when inheritance is enabled.
                    </div>
                </div>

                <div className="form__group">
                    <label htmlFor="alarmPaths" className="form__label">Alarm Paths</label>
                    <div className={getAlarmPathContainerClassName(inheritRules)} ref={pathWrapperRef}>
                        <button
                            id="alarmPaths"
                            type="button"
                            className={`select2-input ${errors?.alarmPaths ? "form__control--error" : ""}`}
                            aria-haspopup="listbox"
                            aria-expanded={openPaths}
                            aria-disabled={inheritRules}
                            disabled={inheritRules}
                            onClick={handleAlarmPathsButtonClick}
                            style={getPathButtonStyle(inheritRules)}
                        >
                            {selectedPathIds.map(pathId => {
                                const path = paths.find(item => Number(item.id) === Number(pathId));

                                return (
                                    <span key={pathId} className="chip">
                                        <span>{getAlarmPathDisplayLabel(path, pathId)}</span>
                                            <span
                                                role="button"
                                                tabIndex={inheritRules ? -1 : 0}
                                                className="chip__remove"
                                                aria-label="remove"
                                                data-path-id={String(pathId)}
                                                onClick={inheritRules ? undefined : handleRemovePathChipClick}
                                                onKeyDown={inheritRules ? undefined : (e => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        handleRemovePathChipClick(e);
                                                    }
                                                })}
                                                aria-disabled={inheritRules}
                                                style={inheritRules ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
                                            ></span>
                                    </span>
                                );
                            })}
                            <span className="select2-input__placeholder">
                                {getAlarmPathPlaceholder(inheritRules, selectedPathIds)}
                            </span>
                        </button>

                        {openPaths && !inheritRules && (
                            <div className="select2-dropdown select2-dropdown--paths">
                                <input
                                    type="text"
                                    className="select2-dropdown__search"
                                    value={pathSearch}
                                    onChange={handlePathSearchChange}
                                    placeholder="Search alarm paths…"
                                />
                                <ul>
                                    {visiblePaths.map(path => {
                                        const id = Number(path.id);
                                        const isSelected = selectedPathIds.includes(id);

                                        return (
                                            <li key={id} className="select2-list-item">
                                                <button type="button" data-path-id={String(id)} onClick={handlePathOptionClick}>
                                                    <span className="select2-list-item__label">{getAlarmPathDisplayLabel(path, id)}</span>
                                                    <span className="select2-list-item__badge">
                                                        {isSelected && <span className="se-icon--acknowledged" aria-hidden="true"></span>}
                                                    </span>
                                                </button>
                                            </li>
                                        );
                                    })}
                                    {paths.length === 0 && (
                                        <li className="select2-list-item">
                                            <div className="text-muted">No alarm paths found for this site.</div>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        )}
                    </div>
                    {errors?.alarmPaths && (
                        <div className="form__error-message">{errors.alarmPaths}</div>
                    )}
                </div>

                {!isModal && (
                    <div className="form__actions">
                        <button className="btn btn--primary" type="submit" disabled={submitting}>
                            {getSubmitLabel(submitting)}
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
};

export default VirtualTenantsForm;
